import { inject, Injectable } from '@angular/core';
import { ODataEntitySetService, ODataServiceFactory } from 'angular-odata';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ProductionSite } from '@workspace-app/odatabackend/QualicoatBackendModel';

export interface ProductionSiteMoveRequest {
  targetCustomerId: string;
  newContactId?: string | null;
  makeMainProductionSite: boolean;
}

export interface ProductionSiteMoveChecklistEntity {
  id: string;
  reference?: string;
  detail?: string;
}

export interface ProductionSiteMoveChecklistItem {
  type: string;
  description: string;
  entities: ProductionSiteMoveChecklistEntity[];
}

export interface ProductionSiteMoveImpact {
  checklist: ProductionSiteMoveChecklistItem[];
  warnings: string[];
  inspectionsToRepoint: number;
  certifiableSystemsToRepoint: number;
}

export interface ProductionSiteMoveResult {
  impact: ProductionSiteMoveImpact;
  repointedInspections: number;
  repointedCertifiableSystems: number;
  closedChangeRequestIds: string[];
}

@Injectable({
  providedIn: 'root',
})
export class ProductionSiteDataService {
  private factory = inject(ODataServiceFactory);
  private httpClient = inject(HttpClient);

  odataServices: ODataEntitySetService<ProductionSite>;

  constructor() {
    this.odataServices = this.factory.entitySet<ProductionSite>(
      'ProductionSites',
      'QualicoatBackend.Domain.Crm.ProductionSite'
    );
  }

  archive(productionSiteId: string) {
    return this.httpClient.post(`/api/production-sites/${productionSiteId}/archive`, {});
  }

  unarchive(productionSiteId: string) {
    return this.httpClient.post(`/api/production-sites/${productionSiteId}/unarchive`, {});
  }

  update(productionSiteId: string, patch: Partial<ProductionSite>) {
    return this.httpClient.put(`/api/production-sites/${productionSiteId}`, patch);
  }

  create(productionSite: Partial<ProductionSite>): Observable<ProductionSite> {
    return this.httpClient.post<ProductionSite>('/api/production-sites', productionSite);
  }

  getMoveImpact(productionSiteId: string, targetCustomerId: string): Observable<ProductionSiteMoveImpact> {
    const params = new HttpParams().set('targetCustomerId', targetCustomerId);
    return this.httpClient.get<ProductionSiteMoveImpact>(`/api/production-sites/${productionSiteId}/move-impact`, {
      params,
    });
  }

  move(productionSiteId: string, request: ProductionSiteMoveRequest): Observable<ProductionSiteMoveResult> {
    return this.httpClient.post<ProductionSiteMoveResult>(`/api/production-sites/${productionSiteId}/move`, request);
  }
}
